import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { companiesAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { formatDate } from '../lib/utils';
import { Plus, Search, Building2, ChevronRight, Phone, Mail, User } from 'lucide-react';

export function Companies() {
    const { isAdmin } = useAuth();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const res = await companiesAPI.getAll();
            setCompanies(res.data);
        } catch (error) {
            console.error('Load error:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredCompanies = companies.filter(company =>
        company.name.toLowerCase().includes(search.toLowerCase()) ||
        company.email?.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-40" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="companies-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Şirketler</h1>
                    <p className="text-slate-500 text-sm mt-1">Müşteri şirketlerinizi yönetin</p>
                </div>
                {isAdmin && (
                    <Link to="/companies/new">
                        <Button className="bg-orwion-green hover:bg-orwion-green-hover" data-testid="create-company-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Yeni Şirket
                        </Button>
                    </Link>
                )}
            </div>

            {/* Search */}
            <Card>
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Şirket ara..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10"
                            data-testid="search-input"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Companies Grid */}
            {filteredCompanies.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredCompanies.map((company) => (
                        <Link key={company.id} to={`/companies/${company.id}`} data-testid={`company-card-${company.id}`}>
                            <Card className="hover:shadow-md transition-all cursor-pointer h-full group">
                                <CardContent className="p-5">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="w-12 h-12 rounded-xl bg-orwion-navy/10 flex items-center justify-center group-hover:bg-orwion-navy/20 transition-colors">
                                            <Building2 className="w-6 h-6 text-orwion-navy" />
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-orwion-green transition-colors" />
                                    </div>

                                    <h3 className="font-semibold text-slate-800 mb-3">{company.name}</h3>

                                    <div className="space-y-2 text-sm text-slate-500">
                                        {company.contact_person && (
                                            <div className="flex items-center gap-2">
                                                <User className="w-3 h-3" />
                                                <span className="truncate">{company.contact_person}</span>
                                            </div>
                                        )}
                                        {company.email && (
                                            <div className="flex items-center gap-2">
                                                <Mail className="w-3 h-3" />
                                                <span className="truncate">{company.email}</span>
                                            </div>
                                        )}
                                        {company.phone && (
                                            <div className="flex items-center gap-2">
                                                <Phone className="w-3 h-3" />
                                                <span>{company.phone}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-400">
                                        Oluşturma: {formatDate(company.created_at)}
                                    </div>
                                </CardContent>
                            </Card>
                        </Link>
                    ))}
                </div>
            ) : (
                <Card>
                    <CardContent className="p-12 text-center">
                        <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <h3 className="font-semibold text-slate-600 mb-1">Şirket Bulunamadı</h3>
                        <p className="text-sm text-slate-400 mb-4">
                            {search ? 'Arama kriterlerine uygun şirket bulunamadı.' : 'Henüz şirket eklenmemiş.'}
                        </p>
                        {isAdmin && (
                            <Link to="/companies/new">
                                <Button className="bg-orwion-green hover:bg-orwion-green-hover" data-testid="empty-create-btn">
                                    <Plus className="w-4 h-4 mr-2" />
                                    İlk Şirketi Ekle
                                </Button>
                            </Link>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

export default Companies;
